import * as Yup from 'yup'
import {validatePhoneField} from '../../../services/Functional'

export interface ICreateAccount {
  user_id: string | undefined
  companyName: string
  companyRegistrationNumber?: string
  vatNumber?: string
  email: string
  phone: string
  country_code: string
  address: string
  city: string
  state: string
  country: string
  zipcode?: string
  sectors: string[]
  subscription_plan?: string
  subscription_type?: string
  plan_type?: string
  selected_plan?: {
    type?: string
  }
  team_size_id?: string
  is_email_verified: boolean
  is_phone_no_verfied: boolean
  is_email_disabled: boolean
  is_phone_no_disabled: boolean
  is_existing_email_user: boolean
  is_existing_phone_user: boolean
  is_mask_email: boolean
  is_mask_phone_no: boolean
  is_already_existing_user: boolean
}

const createAccountSchemas = [
  Yup.object({
    companyName: Yup.string()
      .required()
      .label('Company Name')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid company name'),
    companyRegistrationNumber: Yup.string()
      .nullable()
      .notRequired()
      .transform((v) => (v === '' ? null : v))
      .label('Company Registration Number')
      .matches(/^[A-Za-z0-9\-_/\s.]+$/, 'Please enter a valid registration number')
      .min(3, 'Registration number must be at least 3 characters')
      .max(50, 'Registration number cannot exceed 50 characters'),
    vatNumber: Yup.string()
      .nullable()
      .notRequired()
      .transform((v) => (v === '' ? null : v))
      .label('VAT Number')
      .matches(/^[A-Za-z0-9\-_/\s.]+$/, 'Please enter a valid VAT number')
      .min(3, 'VAT number must be at least 3 characters')
      .max(50, 'VAT number cannot exceed 50 characters'),
    email: Yup.string()
      .required()
      .email('Please enter a valid email')
      .label('Email')
      .test('verifyEmailValidation', 'Your email is not verified', function (value) {
        const {is_email_verified} = this.parent
        if (!is_email_verified) {
          return false
        }
        return true
      }),
    phone: Yup.string()
      .min(8, 'Phone must be at least 8 digit')
      .required('Phone is required')
      .test('formatAndCountryValidation', 'Phone number is not valid', function (value) {
        const {country_code} = this.parent as {country_code: string}
        // values.is_phone_no_verfied
        return validatePhoneField(value || '', country_code)
      })
      .test('verifyPhoneValidation', 'Your phone number is not verified', function (value) {
        const {is_phone_no_verfied} = this.parent
        if (!is_phone_no_verfied) {
          return false
        }
        return true
      }),
    address: Yup.string()
      .required()
      .label('Address')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid address'),
    city: Yup.string()
      .required()
      .label('City')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid city'),
    state: Yup.string()
      .required()
      .label('State')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid state'),
    country: Yup.string()
      .required()
      .label('Country')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid country'),
    zipcode: Yup.string()
      .required('Zipcode is required')
      .test('zipcode', 'Zipcode must be between 4 and 10 digits', (value) => {
        if (!value) return false
        const zipString = String(value)
        return zipString.length >= 4 && zipString.length <= 10
      })
      .nullable(),
  }),
  Yup.object({
    sectors: Yup.array()
      .of(Yup.string())
      .min(1, 'Select at least one industry')
      .required('Industry selection is required'),
  }),
  Yup.object({
    subscription_plan: Yup.string().required('Please select one plan').label('Subscription Plan'),
  }),
]

const dialogValidationSchemas = [
  Yup.object({
    companyName: Yup.string()
      .required()
      .label('Company Name')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid company name'),
    companyRegistrationNumber: Yup.string()
      .nullable()
      .notRequired()
      .transform((v) => (v === '' ? null : v))
      .label('Company Registration Number')
      .matches(/^[A-Za-z0-9\-_/\s.]+$/, 'Please enter a valid registration number')
      .min(3, 'Registration number must be at least 3 characters')
      .max(50, 'Registration number cannot exceed 50 characters'),
    vatNumber: Yup.string()
      .nullable()
      .notRequired()
      .transform((v) => (v === '' ? null : v))
      .label('VAT Number')
      .matches(/^[A-Za-z0-9\-_/\s.]+$/, 'Please enter a valid VAT number')
      .min(3, 'VAT number must be at least 3 characters')
      .max(50, 'VAT number cannot exceed 50 characters'),
    email: Yup.string().required().email('Please enter a valid email').label('Email'),
    phone: Yup.string().min(8, 'Phone must be at least 8 digit').required('Phone is required'),
    address: Yup.string()
      .required()
      .label('Address')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid address'),
    city: Yup.string()
      .required()
      .label('City')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid city'),
    state: Yup.string()
      .required()
      .label('State')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid state'),
    country: Yup.string()
      .required()
      .label('Country')
      .matches(/^(?!\s*$).{3,}$/, 'Please enter a valid country'),
    zipcode: Yup.string()
      .required('Zipcode is required')
      .test('zipcode', 'Zipcode must be between 4 and 10 digits', (value) => {
        if (!value) return false
        const zipString = String(value)
        return zipString.length >= 4 && zipString.length <= 10
      })
      .nullable(),
    sectors: Yup.array()
      .of(Yup.string())
      .min(1, 'Select at least one industry')
      .required('Industry selection is required'),
    team_size_id: Yup.string().required('Select team size'),
  }),
]

const inits: ICreateAccount = {
  user_id: undefined,
  companyName: '',
  companyRegistrationNumber: '',
  vatNumber: '',
  email: '',
  phone: '',
  country_code: '',
  address: '',
  city: '',
  state: '',
  country: '',
  zipcode: undefined,
  sectors: [],
  subscription_plan: undefined,
  subscription_type: 'monthly',
  plan_type: undefined,
  selected_plan: {},
  team_size_id: undefined,
  is_email_disabled: false,
  is_phone_no_disabled: false,
  is_email_verified: false,
  is_phone_no_verfied: false,
  is_existing_email_user: false,
  is_existing_phone_user: false,
  is_mask_email: false,
  is_mask_phone_no: false,
  is_already_existing_user: false,
}

export {createAccountSchemas, inits, dialogValidationSchemas}
